// Antarctica Capital — Helios CapitalMarketsOS Demo Data
import type {
  Loan,
  LoanStage,
  ProductType,
  PipelineMetrics,
  ValuationRecord,
  DevelopmentFeasibility,
} from '@/types';

// ---------------------------------------------------------------------------
// 1. ANTARCTICA_DEALS — 18 CRE deals
// ---------------------------------------------------------------------------

const CURATED_DEALS: Loan[] = [
  // ── ALG-1001 · Received · Multifamily · Senior Mortgage ──────────────
  {
    id: 'ALG-1001',
    property: 'Harbor Point Residences',
    type: 'Multifamily',
    product: 'Senior Mortgage',
    location: 'Miami, FL',
    borrower: 'Trident Residential Partners',
    sponsor: 'Ricardo Medina',
    sponsorCredit: 742,
    loanAmount: 62.5,
    propertyValue: 89.3,
    ltv: 0.70,
    dscr: 1.42,
    debtYield: 0.092,
    stage: 'Triage',
    stageIndex: 0,
    phase: 'Production',
    assignee: 'Sarah Chen',
    daysOpen: 6,
    daysInStatus: 6,
    rate: 'SOFR+375',
    originated: '2026-03-13',
    priority: 'High',
    noi: 5.76,
    sf: 289000,
    units: 340,
    yearBuilt: 2019,
    cashFlows: {
      historicNOI: 5.52,
      prospectiveNOI: 5.76,
      variability: 'Low',
      expenseRatio: 0.38,
      revenue: 9.29,
      expenses: 3.53,
      rents: 8.94,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Miami-Fort Lauderdale-Pompano Beach',
      vacRate: 4.8,
      rentGrowth: 3.7,
      capRate: 5.25,
      absorption: 2850,
    },
    triggers: [],
    documents: ['Loan Application', 'Rent Roll', 'T-12 Operating Statement', 'Property Photos'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 62.5, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.70, maturity: '2031-04-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Unit Mix — Studios (68)', suite: 'A-Block', sf: 40800, monthlyRent: 136000, annualRent: 1632000, rentPerSf: 40.00, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 1BR (170)', suite: 'B-Block', sf: 136000, monthlyRent: 382500, annualRent: 4590000, rentPerSf: 33.75, leaseStart: '2025-03-01', leaseEnd: '2026-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 2BR (102)', suite: 'C-Block', sf: 112200, monthlyRent: 280500, annualRent: 3366000, rentPerSf: 30.00, leaseStart: '2025-06-01', leaseEnd: '2027-05-31', escalation: '3% Annual', status: 'Current' },
    ],
  },

  // ── ALG-1002 · Received · Office · Senior Mortgage ───────────────────
  {
    id: 'ALG-1002',
    property: 'Pinnacle Tower',
    type: 'Office',
    product: 'Senior Mortgage',
    location: 'Dallas, TX',
    borrower: 'Lone Star Office Holdings',
    sponsor: 'James Whitfield',
    sponsorCredit: 718,
    loanAmount: 47.0,
    propertyValue: 72.3,
    ltv: 0.65,
    dscr: 1.58,
    debtYield: 0.098,
    stage: 'Triage',
    stageIndex: 0,
    phase: 'Production',
    assignee: 'Marcus Webb',
    daysOpen: 9,
    daysInStatus: 9,
    rate: '5.85% Fixed',
    originated: '2026-03-10',
    priority: 'Medium',
    noi: 4.61,
    sf: 318000,
    yearBuilt: 2007,
    cashFlows: {
      historicNOI: 4.38,
      prospectiveNOI: 4.61,
      variability: 'Medium',
      expenseRatio: 0.44,
      revenue: 8.23,
      expenses: 3.62,
      rents: 7.85,
      leaseVelocity: 'Moderate',
    },
    market: {
      msa: 'Dallas-Fort Worth-Arlington',
      vacRate: 18.2,
      rentGrowth: 1.1,
      capRate: 7.50,
      absorption: -420,
    },
    triggers: [],
    documents: ['Loan Application', 'Appraisal Request', 'Sponsor Financial Statement'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 47.0, rate: '5.85% Fixed', spread: 300, index: 'UST', ltv: 0.65, maturity: '2036-04-01', term: '10 Year' },
    ],
    rentRoll: [
      { tenant: 'Haynes & Boone LLP', suite: '1400-1600', sf: 68000, monthlyRent: 221000, annualRent: 2652000, rentPerSf: 39.00, leaseStart: '2022-07-01', leaseEnd: '2032-06-30', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Deloitte Advisory', suite: '2200-2400', sf: 52000, monthlyRent: 182000, annualRent: 2184000, rentPerSf: 42.00, leaseStart: '2023-01-01', leaseEnd: '2030-12-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Regus / IWG', suite: '800-900', sf: 24000, monthlyRent: 72000, annualRent: 864000, rentPerSf: 36.00, leaseStart: '2024-06-01', leaseEnd: '2029-05-31', escalation: '2% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: '1000-1100', sf: 31000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
  },

  // ── ALG-1003 · Received · Industrial · Large Loan ────────────────────
  {
    id: 'ALG-1003',
    property: 'Gateway Logistics Center',
    type: 'Industrial',
    product: 'Large Loan',
    location: 'Phoenix, AZ',
    borrower: 'Sunbelt Industrial Trust',
    sponsor: 'David Kowalski',
    sponsorCredit: 768,
    loanAmount: 145.0,
    propertyValue: 207.1,
    ltv: 0.70,
    dscr: 1.51,
    debtYield: 0.083,
    stage: 'Triage',
    stageIndex: 0,
    phase: 'Production',
    assignee: 'Sarah Chen',
    daysOpen: 5,
    daysInStatus: 5,
    rate: 'SOFR+350',
    originated: '2026-03-14',
    priority: 'High',
    noi: 12.03,
    sf: 1240000,
    yearBuilt: 2022,
    cashFlows: {
      historicNOI: 11.45,
      prospectiveNOI: 12.03,
      variability: 'Very Low',
      expenseRatio: 0.22,
      revenue: 15.42,
      expenses: 3.39,
      rents: 14.88,
      leaseVelocity: 'Very Strong',
    },
    market: {
      msa: 'Phoenix-Mesa-Chandler',
      vacRate: 6.1,
      rentGrowth: 4.8,
      capRate: 5.75,
      absorption: 8200,
    },
    triggers: [],
    documents: ['Loan Application', 'Phase I ESA', 'Rent Roll', 'Property Condition Report'],
    capitalStack: [
      { type: 'Large Loan', amount: 145.0, rate: 'SOFR+350', spread: 350, index: 'SOFR', ltv: 0.70, maturity: '2033-04-01', term: '7 Year' },
    ],
    rentRoll: [
      { tenant: 'Amazon Logistics', suite: 'Bldg A', sf: 520000, monthlyRent: 520000, annualRent: 6240000, rentPerSf: 12.00, leaseStart: '2022-09-01', leaseEnd: '2032-08-31', escalation: '2.75% Annual', status: 'Current' },
      { tenant: 'FedEx Ground', suite: 'Bldg B', sf: 380000, monthlyRent: 360500, annualRent: 4326000, rentPerSf: 11.38, leaseStart: '2023-01-01', leaseEnd: '2033-12-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'XPO Logistics', suite: 'Bldg C', sf: 210000, monthlyRent: 210000, annualRent: 2520000, rentPerSf: 12.00, leaseStart: '2024-03-01', leaseEnd: '2031-02-28', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: 'Bldg D-Partial', sf: 130000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
  },

  // ── ALG-1004 · Quoted · Multifamily · Senior Mortgage ────────────────
  {
    id: 'ALG-1004',
    property: 'The Sinclair on Peachtree',
    type: 'Multifamily',
    product: 'Senior Mortgage',
    location: 'Atlanta, GA',
    borrower: 'Magnolia Residential Group',
    sponsor: 'Vanessa Okafor',
    sponsorCredit: 755,
    loanAmount: 38.5,
    propertyValue: 55.0,
    ltv: 0.70,
    dscr: 1.47,
    debtYield: 0.094,
    stage: 'Post-screen Quote',
    stageIndex: 2,
    phase: 'Production',
    assignee: 'Derek Hoffman',
    daysOpen: 18,
    daysInStatus: 5,
    rate: 'SOFR+375',
    originated: '2026-03-01',
    priority: 'Medium',
    noi: 3.63,
    sf: 195500,
    units: 230,
    yearBuilt: 2016,
    cashFlows: {
      historicNOI: 3.41,
      prospectiveNOI: 3.63,
      variability: 'Low',
      expenseRatio: 0.40,
      revenue: 6.05,
      expenses: 2.42,
      rents: 5.82,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Atlanta-Sandy Springs-Alpharetta',
      vacRate: 6.3,
      rentGrowth: 2.9,
      capRate: 5.50,
      absorption: 3100,
    },
    triggers: [],
    documents: ['Term Sheet', 'Rent Roll', 'T-12 Operating Statement', 'Sponsor Bio', 'Property Photos'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 38.5, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.70, maturity: '2031-04-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Unit Mix — 1BR (138)', suite: 'Bldgs 1-3', sf: 110400, monthlyRent: 248400, annualRent: 2980800, rentPerSf: 27.00, leaseStart: '2025-06-01', leaseEnd: '2026-11-30', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 2BR (92)', suite: 'Bldgs 4-5', sf: 85100, monthlyRent: 193200, annualRent: 2318400, rentPerSf: 27.24, leaseStart: '2025-08-01', leaseEnd: '2027-01-31', escalation: '3% Annual', status: 'Current' },
    ],
  },

  // ── ALG-1005 · Quoted · Hotel · B-Note ───────────────────────────────
  {
    id: 'ALG-1005',
    property: 'The Langford Hotel',
    type: 'Hospitality',
    product: 'B-Note',
    location: 'Miami, FL',
    borrower: 'Riviera Hospitality Ventures',
    sponsor: 'Anton Perez',
    sponsorCredit: 695,
    loanAmount: 18.5,
    propertyValue: 78.0,
    ltv: 0.81,
    dscr: 1.22,
    debtYield: 0.078,
    stage: 'Post-screen Quote',
    stageIndex: 2,
    phase: 'Production',
    assignee: 'Sarah Chen',
    daysOpen: 14,
    daysInStatus: 4,
    rate: 'SOFR+600',
    originated: '2026-03-05',
    priority: 'Medium',
    noi: 6.08,
    sf: 142000,
    yearBuilt: 1926,
    cashFlows: {
      historicNOI: 5.74,
      prospectiveNOI: 6.08,
      variability: 'High',
      expenseRatio: 0.62,
      revenue: 16.00,
      expenses: 9.92,
      rents: 14.40,
      leaseVelocity: 'Moderate',
    },
    market: {
      msa: 'Miami-Fort Lauderdale-Pompano Beach',
      vacRate: 22.5,
      rentGrowth: 1.8,
      capRate: 8.25,
      absorption: 650,
    },
    triggers: [],
    documents: ['Term Sheet', 'STR Report', 'Franchise Agreement', 'PIP Estimate', 'Sponsor Financials'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 46.8, rate: 'SOFR+325', spread: 325, index: 'SOFR', ltv: 0.60, maturity: '2031-04-01', term: '5 Year' },
      { type: 'B-Note', amount: 18.5, rate: 'SOFR+600', spread: 600, index: 'SOFR', ltv: 0.81, maturity: '2031-04-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Marriott Autograph (126 keys)', suite: 'All Floors', sf: 142000, monthlyRent: 1200000, annualRent: 14400000, rentPerSf: 101.41, leaseStart: '2020-01-01', leaseEnd: '2040-12-31', escalation: 'CPI + 1%', status: 'Current' },
    ],
  },

  // ── ALG-1006 · Quoted · Mixed-Use · Senior Mortgage ──────────────────
  {
    id: 'ALG-1006',
    property: 'Fulton Market Exchange',
    type: 'Mixed-Use',
    product: 'Senior Mortgage',
    location: 'Chicago, IL',
    borrower: 'Lakeshore Capital Partners',
    sponsor: 'Catherine Brennan',
    sponsorCredit: 761,
    loanAmount: 54.0,
    propertyValue: 81.8,
    ltv: 0.66,
    dscr: 1.53,
    debtYield: 0.091,
    stage: 'Post-screen Quote',
    stageIndex: 2,
    phase: 'Production',
    assignee: 'Marcus Webb',
    daysOpen: 22,
    daysInStatus: 6,
    rate: '6.15% Fixed',
    originated: '2026-02-25',
    priority: 'Low',
    noi: 4.93,
    sf: 186000,
    units: 72,
    yearBuilt: 2021,
    cashFlows: {
      historicNOI: 4.68,
      prospectiveNOI: 4.93,
      variability: 'Low',
      expenseRatio: 0.36,
      revenue: 7.70,
      expenses: 2.77,
      rents: 7.31,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Chicago-Naperville-Elgin',
      vacRate: 12.8,
      rentGrowth: 1.5,
      capRate: 6.50,
      absorption: 180,
    },
    triggers: [],
    documents: ['Term Sheet', 'Rent Roll', 'T-12', 'Appraisal Draft', 'Phase I ESA'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 54.0, rate: '6.15% Fixed', spread: 300, index: 'UST', ltv: 0.66, maturity: '2036-03-01', term: '10 Year' },
    ],
    rentRoll: [
      { tenant: 'Residential (72 units)', suite: 'Floors 3-8', sf: 64800, monthlyRent: 187200, annualRent: 2246400, rentPerSf: 34.67, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'WeWork', suite: 'Floor 2', sf: 42000, monthlyRent: 126000, annualRent: 1512000, rentPerSf: 36.00, leaseStart: '2021-09-01', leaseEnd: '2031-08-31', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Whole Foods Market', suite: 'Ground', sf: 38000, monthlyRent: 142500, annualRent: 1710000, rentPerSf: 45.00, leaseStart: '2021-06-01', leaseEnd: '2036-05-31', escalation: '10% every 5 yrs', status: 'Current' },
      { tenant: 'Sweetgreen', suite: 'Ground-B', sf: 3200, monthlyRent: 16000, annualRent: 192000, rentPerSf: 60.00, leaseStart: '2022-03-01', leaseEnd: '2032-02-28', escalation: '3% Annual', status: 'Current' },
    ],
  },

  // ── ALG-1007 · Application Issued · Industrial · Senior Mortgage ─────
  {
    id: 'ALG-1007',
    property: 'Inland Empire Distribution Hub',
    type: 'Industrial',
    product: 'Senior Mortgage',
    location: 'Los Angeles, CA',
    borrower: 'Pacific Logistics Realty',
    sponsor: 'Thomas Nakamura',
    sponsorCredit: 782,
    loanAmount: 72.0,
    propertyValue: 102.9,
    ltv: 0.70,
    dscr: 1.62,
    debtYield: 0.088,
    stage: 'Term Sheet Issued',
    stageIndex: 3,
    phase: 'Production',
    assignee: 'Derek Hoffman',
    daysOpen: 31,
    daysInStatus: 7,
    rate: 'SOFR+375',
    originated: '2026-02-16',
    priority: 'High',
    noi: 6.34,
    sf: 485000,
    yearBuilt: 2020,
    cashFlows: {
      historicNOI: 6.01,
      prospectiveNOI: 6.34,
      variability: 'Very Low',
      expenseRatio: 0.19,
      revenue: 7.83,
      expenses: 1.49,
      rents: 7.56,
      leaseVelocity: 'Very Strong',
    },
    market: {
      msa: 'Los Angeles-Long Beach-Anaheim',
      vacRate: 3.2,
      rentGrowth: 5.1,
      capRate: 5.00,
      absorption: 6400,
    },
    triggers: [],
    documents: ['Application Package', 'Appraisal Engagement', 'Phase I ESA', 'Rent Roll', 'T-12', 'Sponsor Financials', 'Entity Docs'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 72.0, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.70, maturity: '2031-03-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Home Depot Supply Chain', suite: 'Bldg 1', sf: 260000, monthlyRent: 282750, annualRent: 3393000, rentPerSf: 13.05, leaseStart: '2021-01-01', leaseEnd: '2031-12-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Kuehne+Nagel', suite: 'Bldg 2', sf: 155000, monthlyRent: 161875, annualRent: 1942500, rentPerSf: 12.53, leaseStart: '2022-06-01', leaseEnd: '2029-05-31', escalation: '2.75% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: 'Bldg 3', sf: 70000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.62, resultingLtv: 0.70, resultingDebtYield: 0.088, pass: true },
      { name: 'Downside', vacancyShock: 0.15, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.31, resultingLtv: 0.78, resultingDebtYield: 0.071, pass: true },
      { name: 'Severe', vacancyShock: 0.25, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 1.08, resultingLtv: 0.88, resultingDebtYield: 0.058, pass: false },
    ],
  },

  // ── ALG-1008 · Application Issued · Senior Living · Mezzanine ────────
  {
    id: 'ALG-1008',
    property: 'Sunrise Gardens Senior Living',
    type: 'Senior Living',
    product: 'Mezzanine',
    location: 'Denver, CO',
    borrower: 'Evergreen Senior Care REIT',
    sponsor: 'Margaret Holloway',
    sponsorCredit: 724,
    loanAmount: 22.0,
    propertyValue: 115.0,
    ltv: 0.82,
    dscr: 1.19,
    debtYield: 0.074,
    stage: 'Term Sheet Issued',
    stageIndex: 3,
    phase: 'Production',
    assignee: 'Lisa Tran',
    daysOpen: 27,
    daysInStatus: 6,
    rate: 'SOFR+600',
    originated: '2026-02-20',
    priority: 'Medium',
    noi: 7.76,
    sf: 198000,
    units: 220,
    yearBuilt: 2014,
    cashFlows: {
      historicNOI: 7.42,
      prospectiveNOI: 7.76,
      variability: 'Medium',
      expenseRatio: 0.58,
      revenue: 18.48,
      expenses: 10.72,
      rents: 17.14,
      leaseVelocity: 'Moderate',
    },
    market: {
      msa: 'Denver-Aurora-Lakewood',
      vacRate: 11.5,
      rentGrowth: 2.2,
      capRate: 6.75,
      absorption: 420,
    },
    triggers: [],
    documents: ['Application Package', 'Appraisal Engagement', 'Sponsor Financials', 'License & Regulatory Docs', 'Census Data'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 72.5, rate: 'SOFR+350', spread: 350, index: 'SOFR', ltv: 0.63, maturity: '2031-03-01', term: '5 Year' },
      { type: 'Mezzanine', amount: 22.0, rate: 'SOFR+600', spread: 600, index: 'SOFR', ltv: 0.82, maturity: '2031-03-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Independent Living (110 units)', suite: 'Wing A', sf: 88000, monthlyRent: 577500, annualRent: 6930000, rentPerSf: 78.75, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: 'CPI + 2%', status: 'Current' },
      { tenant: 'Assisted Living (80 units)', suite: 'Wing B', sf: 72000, monthlyRent: 520000, annualRent: 6240000, rentPerSf: 86.67, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: 'CPI + 2%', status: 'Current' },
      { tenant: 'Memory Care (30 units)', suite: 'Wing C', sf: 38000, monthlyRent: 285000, annualRent: 3420000, rentPerSf: 90.00, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: 'CPI + 2%', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.19, resultingLtv: 0.82, resultingDebtYield: 0.074, pass: true },
      { name: 'Downside', vacancyShock: 0.12, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.02, resultingLtv: 0.90, resultingDebtYield: 0.061, pass: true },
      { name: 'Severe', vacancyShock: 0.20, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 0.88, resultingLtv: 1.02, resultingDebtYield: 0.049, pass: false },
    ],
  },

  // ── ALG-1009 · Application Back · Retail · Ground Lease ──────────────
  {
    id: 'ALG-1009',
    property: 'Galleria Commons',
    type: 'Retail',
    product: 'Ground Lease',
    location: 'Dallas, TX',
    borrower: 'Brazos Retail Investors',
    sponsor: 'William Hargrove',
    sponsorCredit: 738,
    loanAmount: 28.5,
    propertyValue: 71.3,
    ltv: 0.40,
    dscr: 1.85,
    debtYield: 0.118,
    stage: 'Term Sheet Signed',
    stageIndex: 4,
    phase: 'Production',
    assignee: 'Marcus Webb',
    daysOpen: 42,
    daysInStatus: 8,
    rate: '5.75% Cap Rate',
    originated: '2026-02-05',
    priority: 'Low',
    noi: 3.37,
    sf: 224000,
    yearBuilt: 2003,
    cashFlows: {
      historicNOI: 3.21,
      prospectiveNOI: 3.37,
      variability: 'Low',
      expenseRatio: 0.28,
      revenue: 4.68,
      expenses: 1.31,
      rents: 4.45,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Dallas-Fort Worth-Arlington',
      vacRate: 5.8,
      rentGrowth: 2.1,
      capRate: 6.50,
      absorption: 1200,
    },
    triggers: [],
    documents: ['Signed Application', 'Ground Lease Agreement', 'Appraisal', 'Phase I ESA', 'T-12', 'Tenant Estoppels'],
    capitalStack: [
      { type: 'Ground Lease', amount: 28.5, rate: '5.75% Cap Rate', spread: 0, index: 'Fixed', ltv: 0.40, maturity: '2091-01-01', term: '65 Year' },
    ],
    rentRoll: [
      { tenant: 'Target', suite: 'Anchor-A', sf: 125000, monthlyRent: 156250, annualRent: 1875000, rentPerSf: 15.00, leaseStart: '2004-01-01', leaseEnd: '2034-12-31', escalation: '10% every 5 yrs', status: 'Current' },
      { tenant: 'Marshalls', suite: '200', sf: 28000, monthlyRent: 49000, annualRent: 588000, rentPerSf: 21.00, leaseStart: '2018-03-01', leaseEnd: '2028-02-28', escalation: 'Flat', status: 'Current' },
      { tenant: 'Starbucks (pad)', suite: 'Pad-1', sf: 2200, monthlyRent: 11000, annualRent: 132000, rentPerSf: 60.00, leaseStart: '2019-06-01', leaseEnd: '2029-05-31', escalation: '2% Annual', status: 'Current' },
      { tenant: 'Chick-fil-A (pad)', suite: 'Pad-2', sf: 4500, monthlyRent: 18750, annualRent: 225000, rentPerSf: 50.00, leaseStart: '2020-01-01', leaseEnd: '2040-12-31', escalation: '10% every 5 yrs', status: 'Current' },
      { tenant: 'Vacant inline', suite: '310-318', sf: 12000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.85, resultingLtv: 0.40, resultingDebtYield: 0.118, pass: true },
      { name: 'Downside', vacancyShock: 0.15, rentDecline: 0.05, capRateExpansion: 0.75, resultingDscr: 1.52, resultingLtv: 0.46, resultingDebtYield: 0.094, pass: true },
      { name: 'Severe', vacancyShock: 0.25, rentDecline: 0.12, capRateExpansion: 1.50, resultingDscr: 1.18, resultingLtv: 0.55, resultingDebtYield: 0.072, pass: true },
    ],
  },

  // ── ALG-1010 · Application Back · Office · Senior Mortgage ───────────
  {
    id: 'ALG-1010',
    property: 'Meridian Tower',
    type: 'Office',
    product: 'Senior Mortgage',
    location: 'New York, NY',
    borrower: 'Blackbridge Capital Group',
    sponsor: 'Jonathan Alderman',
    sponsorCredit: 789,
    loanAmount: 95.0,
    propertyValue: 146.2,
    ltv: 0.65,
    dscr: 1.45,
    debtYield: 0.085,
    stage: 'Term Sheet Signed',
    stageIndex: 4,
    phase: 'Production',
    assignee: 'Sarah Chen',
    daysOpen: 38,
    daysInStatus: 7,
    rate: '6.25% Fixed',
    originated: '2026-02-09',
    priority: 'High',
    noi: 8.07,
    sf: 410000,
    yearBuilt: 1988,
    cashFlows: {
      historicNOI: 7.68,
      prospectiveNOI: 8.07,
      variability: 'Medium',
      expenseRatio: 0.48,
      revenue: 15.52,
      expenses: 7.45,
      rents: 14.82,
      leaseVelocity: 'Moderate',
    },
    market: {
      msa: 'New York-Newark-Jersey City',
      vacRate: 16.4,
      rentGrowth: 0.8,
      capRate: 6.80,
      absorption: -1200,
    },
    triggers: [
      { id: 'T-1010-1', type: 'Appraisal', due: '2026-03-25', status: 'pending', severity: 'medium', description: 'Appraisal draft expected from CBRE' },
    ],
    documents: ['Signed Application', 'Appraisal Engagement', 'Phase I ESA', 'Phase II ESA', 'T-12', 'Sponsor Financials', 'Title Report', 'Zoning Letter'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 95.0, rate: '6.25% Fixed', spread: 300, index: 'UST', ltv: 0.65, maturity: '2041-03-01', term: '15 Year' },
    ],
    rentRoll: [
      { tenant: 'McKinsey & Company', suite: '3000-3400', sf: 86000, monthlyRent: 502000, annualRent: 6024000, rentPerSf: 70.05, leaseStart: '2019-01-01', leaseEnd: '2034-12-31', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Sullivan & Cromwell', suite: '2200-2600', sf: 72000, monthlyRent: 420000, annualRent: 5040000, rentPerSf: 70.00, leaseStart: '2020-06-01', leaseEnd: '2035-05-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Citadel Securities', suite: '4000-4200', sf: 48000, monthlyRent: 340000, annualRent: 4080000, rentPerSf: 85.00, leaseStart: '2023-09-01', leaseEnd: '2033-08-31', escalation: '2.75% Annual', status: 'Current' },
      { tenant: 'Marsh & McLennan', suite: '1800-1900', sf: 34000, monthlyRent: 187000, annualRent: 2244000, rentPerSf: 66.00, leaseStart: '2017-04-01', leaseEnd: '2027-03-31', escalation: '2% Annual', status: 'MTM' },
      { tenant: 'Vacant', suite: '1200-1400', sf: 52000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.45, resultingLtv: 0.65, resultingDebtYield: 0.085, pass: true },
      { name: 'Downside', vacancyShock: 0.20, rentDecline: 0.08, capRateExpansion: 0.75, resultingDscr: 1.09, resultingLtv: 0.78, resultingDebtYield: 0.063, pass: true },
      { name: 'Severe', vacancyShock: 0.30, rentDecline: 0.15, capRateExpansion: 1.50, resultingDscr: 0.84, resultingLtv: 0.95, resultingDebtYield: 0.046, pass: false },
    ],
  },

  // ── ALG-1011 · Commitment Issued · Multifamily · Senior Mortgage (combo A) ─
  {
    id: 'ALG-1011',
    property: 'The Ellington at Uptown',
    type: 'Multifamily',
    product: 'Senior Mortgage',
    location: 'Dallas, TX',
    borrower: 'Crescent Multifamily Advisors',
    sponsor: 'Robert Stanton',
    sponsorCredit: 752,
    loanAmount: 56.0,
    propertyValue: 84.8,
    ltv: 0.66,
    dscr: 1.55,
    debtYield: 0.093,
    stage: 'IC / Committee Approved',
    stageIndex: 5,
    phase: 'Production',
    assignee: 'Derek Hoffman',
    daysOpen: 62,
    daysInStatus: 10,
    rate: 'SOFR+375',
    originated: '2026-01-16',
    priority: 'High',
    noi: 5.21,
    sf: 263500,
    units: 310,
    yearBuilt: 2017,
    cashFlows: {
      historicNOI: 4.94,
      prospectiveNOI: 5.21,
      variability: 'Low',
      expenseRatio: 0.39,
      revenue: 8.54,
      expenses: 3.33,
      rents: 8.21,
      leaseVelocity: 'Very Strong',
    },
    market: {
      msa: 'Dallas-Fort Worth-Arlington',
      vacRate: 7.2,
      rentGrowth: 3.4,
      capRate: 5.35,
      absorption: 4200,
    },
    triggers: [
      { id: 'T-1011-1', type: 'Commitment Expiry', due: '2026-04-15', status: 'upcoming', severity: 'high', description: 'Commitment expires — borrower must countersign' },
    ],
    documents: ['Commitment Letter', 'Appraisal', 'Phase I ESA', 'Property Condition Report', 'Zoning Compliance', 'Insurance Binder', 'T-12', 'Rent Roll'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 56.0, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.66, maturity: '2031-02-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Unit Mix — Studios (62)', suite: 'Tower A', sf: 34100, monthlyRent: 105400, annualRent: 1264800, rentPerSf: 37.09, leaseStart: '2025-06-01', leaseEnd: '2026-11-30', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 1BR (155)', suite: 'Tower A-B', sf: 124000, monthlyRent: 294500, annualRent: 3534000, rentPerSf: 28.50, leaseStart: '2025-03-01', leaseEnd: '2026-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 2BR (93)', suite: 'Tower B', sf: 105400, monthlyRent: 218250, annualRent: 2619000, rentPerSf: 24.85, leaseStart: '2025-09-01', leaseEnd: '2027-02-28', escalation: '3% Annual', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.55, resultingLtv: 0.66, resultingDebtYield: 0.093, pass: true },
      { name: 'Downside', vacancyShock: 0.12, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.30, resultingLtv: 0.74, resultingDebtYield: 0.076, pass: true },
      { name: 'Severe', vacancyShock: 0.20, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 1.05, resultingLtv: 0.85, resultingDebtYield: 0.060, pass: false },
    ],
    committeeMemo: {
      dealId: 'ALG-1011',
      status: 'Approved',
      preparedBy: 'Derek Hoffman',
      committeeDate: '2026-01-10',
      riskRating: 'Low',
      conditions: ['Appraisal at or above $84M', 'Escrow 12 months tax & insurance', 'Rate cap agreement at closing'],
      approvers: [
        { name: 'Patricia Dunn', role: 'Chief Credit Officer', voted: true, vote: 'Approve' },
        { name: 'Michael Torres', role: 'Head of Originations', voted: true, vote: 'Approve' },
        { name: 'Angela Reeves', role: 'Risk Committee Chair', voted: true, vote: 'Conditional' },
      ],
      sections: {
        executiveSummary: 'Requesting $56M senior mortgage for 310-unit Class A multifamily in Dallas Uptown submarket. Strong sponsorship, 96% occupancy, institutional-quality asset.',
        propertyDescription: '310-unit luxury garden-style apartment community built 2017, featuring resort-style amenities, structured parking, and proximity to major employment centers.',
        marketAnalysis: 'Dallas-Fort Worth multifamily market shows healthy fundamentals with 3.4% rent growth, though new supply pipeline warrants monitoring.',
        cashFlowSummary: 'Stabilized NOI of $5.21M supported by strong in-place rents and 96% occupancy. Expense ratio of 39% in line with market.',
        structure: 'Five-year floating rate senior mortgage at SOFR+375bps with 1% SOFR floor. IO for 24 months, 30-year amortization thereafter.',
        riskFactors: 'New supply in Dallas submarket; floating rate exposure mitigated by rate cap requirement; sponsor concentration in DFW market.',
        recommendation: 'Approve subject to standard conditions and rate cap at closing.',
      },
    },
  },

  // ── ALG-1012 · Commitment Issued · Hotel · Senior Mortgage ───────────
  {
    id: 'ALG-1012',
    property: 'The Strand Hotel Seattle',
    type: 'Hospitality',
    product: 'Senior Mortgage',
    location: 'Seattle, WA',
    borrower: 'Cascade Hospitality Group',
    sponsor: 'Emily Richardson',
    sponsorCredit: 731,
    loanAmount: 41.0,
    propertyValue: 62.1,
    ltv: 0.66,
    dscr: 1.38,
    debtYield: 0.089,
    stage: 'IC / Committee Approved',
    stageIndex: 5,
    phase: 'Production',
    assignee: 'Lisa Tran',
    daysOpen: 55,
    daysInStatus: 8,
    rate: 'SOFR+400',
    originated: '2026-01-23',
    priority: 'Medium',
    noi: 3.65,
    sf: 168000,
    yearBuilt: 2015,
    cashFlows: {
      historicNOI: 3.38,
      prospectiveNOI: 3.65,
      variability: 'High',
      expenseRatio: 0.60,
      revenue: 9.13,
      expenses: 5.48,
      rents: 8.42,
      leaseVelocity: 'Moderate',
    },
    market: {
      msa: 'Seattle-Tacoma-Bellevue',
      vacRate: 24.1,
      rentGrowth: 1.3,
      capRate: 7.80,
      absorption: 380,
    },
    triggers: [
      { id: 'T-1012-1', type: 'Franchise Review', due: '2026-03-28', status: 'pending', severity: 'medium', description: 'Hilton franchise approval pending' },
    ],
    documents: ['Commitment Letter', 'Appraisal', 'STR Report', 'Franchise Agreement', 'PIP Estimate', 'Phase I ESA', 'Insurance Binder'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 41.0, rate: 'SOFR+400', spread: 400, index: 'SOFR', ltv: 0.66, maturity: '2031-02-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Curio by Hilton (218 keys)', suite: 'Full Service', sf: 168000, monthlyRent: 701667, annualRent: 8420000, rentPerSf: 50.12, leaseStart: '2015-09-01', leaseEnd: '2035-08-31', escalation: 'CPI', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.38, resultingLtv: 0.66, resultingDebtYield: 0.089, pass: true },
      { name: 'Downside', vacancyShock: 0.15, rentDecline: 0.10, capRateExpansion: 0.75, resultingDscr: 1.06, resultingLtv: 0.80, resultingDebtYield: 0.065, pass: true },
      { name: 'Severe', vacancyShock: 0.30, rentDecline: 0.20, capRateExpansion: 1.50, resultingDscr: 0.78, resultingLtv: 0.99, resultingDebtYield: 0.044, pass: false },
    ],
    committeeMemo: {
      dealId: 'ALG-1012',
      status: 'Approved',
      preparedBy: 'Lisa Tran',
      committeeDate: '2026-01-18',
      riskRating: 'Moderate',
      conditions: ['Hilton franchise approval', 'PIP completion escrow of $2.5M', 'Minimum FF&E reserve of 4% gross revenue'],
      approvers: [
        { name: 'Patricia Dunn', role: 'Chief Credit Officer', voted: true, vote: 'Conditional' },
        { name: 'Michael Torres', role: 'Head of Originations', voted: true, vote: 'Approve' },
        { name: 'Angela Reeves', role: 'Risk Committee Chair', voted: true, vote: 'Conditional' },
      ],
      sections: {
        executiveSummary: 'Requesting $41M senior mortgage for 218-key full-service hotel in downtown Seattle. Curio by Hilton flag with strong convention demand drivers.',
        propertyDescription: '218-key full-service hotel built 2015 near Pike Place Market. Recently renovated lobby and F&B outlets. Full conference facilities.',
        marketAnalysis: 'Seattle hospitality market recovering post-pandemic. Convention center expansion driving demand. New supply limited in downtown core.',
        cashFlowSummary: 'Trailing 12-month RevPAR of $185. NOI of $3.65M with 60% expense ratio typical for full-service hotel.',
        structure: 'Five-year floating rate at SOFR+400bps. Amortizing on 25-year schedule. Cash management with soft lockbox.',
        riskFactors: 'Hospitality cyclicality; tech sector employment sensitivity; PIP requirement of $2.5M within 24 months.',
        recommendation: 'Approve conditional on franchise approval and PIP escrow.',
      },
    },
  },

  // ── ALG-1013 · Commitment Received · Lab/R&D · Large Loan ────────────
  {
    id: 'ALG-1013',
    property: 'Kendall Square Innovation Center',
    type: 'Lab/R&D',
    product: 'Large Loan',
    location: 'Boston, MA',
    borrower: 'Vertex Life Science Properties',
    sponsor: 'Dr. Alan Whitmore',
    sponsorCredit: 776,
    loanAmount: 185.0,
    propertyValue: 264.3,
    ltv: 0.70,
    dscr: 1.48,
    debtYield: 0.082,
    stage: 'IC / Committee Approved',
    stageIndex: 5,
    phase: 'Production',
    assignee: 'Sarah Chen',
    daysOpen: 78,
    daysInStatus: 12,
    rate: 'SOFR+350',
    originated: '2025-12-31',
    priority: 'High',
    noi: 15.17,
    sf: 345000,
    yearBuilt: 2023,
    cashFlows: {
      historicNOI: 14.22,
      prospectiveNOI: 15.17,
      variability: 'Low',
      expenseRatio: 0.32,
      revenue: 22.31,
      expenses: 7.14,
      rents: 21.58,
      leaseVelocity: 'Very Strong',
    },
    market: {
      msa: 'Boston-Cambridge-Newton',
      vacRate: 8.7,
      rentGrowth: 4.2,
      capRate: 5.50,
      absorption: 1800,
    },
    triggers: [
      { id: 'T-1013-1', type: 'Rate Lock Deadline', due: '2026-04-10', status: 'upcoming', severity: 'high', description: 'Borrower must elect rate lock by deadline' },
    ],
    documents: ['Countersigned Commitment', 'Appraisal', 'Phase I ESA', 'ALTA Survey', 'Title Insurance Commitment', 'Rent Roll', 'T-12', 'Sponsor Financials', 'Lab Fit-Out Specs'],
    capitalStack: [
      { type: 'Large Loan', amount: 185.0, rate: 'SOFR+350', spread: 350, index: 'SOFR', ltv: 0.70, maturity: '2033-01-01', term: '7 Year' },
    ],
    rentRoll: [
      { tenant: 'Moderna Therapeutics', suite: 'Floors 1-4', sf: 138000, monthlyRent: 920000, annualRent: 11040000, rentPerSf: 80.00, leaseStart: '2023-06-01', leaseEnd: '2038-05-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Sarepta Therapeutics', suite: 'Floors 5-6', sf: 69000, monthlyRent: 488750, annualRent: 5865000, rentPerSf: 85.00, leaseStart: '2024-01-01', leaseEnd: '2036-12-31', escalation: '2.75% Annual', status: 'Current' },
      { tenant: 'Flagship Pioneering', suite: 'Floor 7', sf: 34500, monthlyRent: 258750, annualRent: 3105000, rentPerSf: 90.00, leaseStart: '2023-09-01', leaseEnd: '2033-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Incubator Space', suite: 'Floor 8-partial', sf: 18000, monthlyRent: 112500, annualRent: 1350000, rentPerSf: 75.00, leaseStart: '2025-01-01', leaseEnd: '2027-12-31', escalation: '2% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: 'Floor 8-remaining', sf: 12500, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.48, resultingLtv: 0.70, resultingDebtYield: 0.082, pass: true },
      { name: 'Downside', vacancyShock: 0.15, rentDecline: 0.08, capRateExpansion: 0.75, resultingDscr: 1.14, resultingLtv: 0.83, resultingDebtYield: 0.061, pass: true },
      { name: 'Severe', vacancyShock: 0.25, rentDecline: 0.15, capRateExpansion: 1.25, resultingDscr: 0.88, resultingLtv: 0.98, resultingDebtYield: 0.044, pass: false },
    ],
    committeeMemo: {
      dealId: 'ALG-1013',
      status: 'Approved',
      preparedBy: 'Sarah Chen',
      committeeDate: '2025-12-20',
      riskRating: 'Low',
      conditions: ['Minimum 90% leased at closing', 'Moderna lease extension confirmation', 'Lab equipment lien subordination'],
      approvers: [
        { name: 'Patricia Dunn', role: 'Chief Credit Officer', voted: true, vote: 'Approve' },
        { name: 'Michael Torres', role: 'Head of Originations', voted: true, vote: 'Approve' },
        { name: 'Angela Reeves', role: 'Risk Committee Chair', voted: true, vote: 'Approve' },
      ],
      sections: {
        executiveSummary: '$185M large loan for premier life science campus in Kendall Square, Cambridge. Trophy asset with 96% occupancy and credit tenancy.',
        propertyDescription: '345,000 SF purpose-built lab and office campus completed 2023. Class A+ specifications with BSL-2 capable lab suites.',
        marketAnalysis: 'Cambridge/Kendall Square remains the premier US life science cluster with constrained supply and institutional demand.',
        cashFlowSummary: 'Stabilized NOI of $15.17M with strong embedded rent growth from contractual escalations. Weighted avg remaining lease term of 10.2 years.',
        structure: 'Seven-year floating rate at SOFR+350bps. Interest-only for full term. Cash trap at 1.20x DSCR.',
        riskFactors: 'Life science sector concentration; single-campus exposure; biotech funding environment sensitivity.',
        recommendation: 'Strong approval. Trophy asset with credit tenancy and long-duration leases.',
      },
    },
  },

  // ── ALG-1014 · Rate Lock · Mixed-Use · Senior Mortgage ───────────────
  {
    id: 'ALG-1014',
    property: 'The Brickell Promenade',
    type: 'Mixed-Use',
    product: 'Senior Mortgage',
    location: 'Miami, FL',
    borrower: 'Coral Gables Development Group',
    sponsor: 'Sofia Gutierrez',
    sponsorCredit: 745,
    loanAmount: 78.0,
    propertyValue: 111.4,
    ltv: 0.70,
    dscr: 1.40,
    debtYield: 0.086,
    stage: 'Rate Lock',
    stageIndex: 6,
    phase: 'Trading',
    assignee: 'Marcus Webb',
    daysOpen: 95,
    daysInStatus: 13,
    rate: '5.92% Fixed',
    originated: '2025-12-14',
    priority: 'High',
    noi: 6.71,
    sf: 265000,
    units: 180,
    yearBuilt: 2022,
    cashFlows: {
      historicNOI: 6.35,
      prospectiveNOI: 6.71,
      variability: 'Low',
      expenseRatio: 0.37,
      revenue: 10.65,
      expenses: 3.94,
      rents: 10.18,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Miami-Fort Lauderdale-Pompano Beach',
      vacRate: 5.1,
      rentGrowth: 3.9,
      capRate: 5.40,
      absorption: 3400,
    },
    triggers: [
      { id: 'T-1014-1', type: 'Rate Lock Expiry', due: '2026-04-14', status: 'upcoming', severity: 'high', description: 'Rate lock expires — must close or extend' },
      { id: 'T-1014-2', type: 'Title Clearance', due: '2026-03-22', status: 'pending', severity: 'medium', description: 'Title exception for easement needs resolution' },
    ],
    documents: ['Rate Lock Confirmation', 'Appraisal', 'Title Insurance Commitment', 'ALTA Survey', 'Closing Instructions', 'Borrower Entity Docs', 'Opinion of Counsel'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 78.0, rate: '5.92% Fixed', spread: 300, index: 'UST', ltv: 0.70, maturity: '2036-01-01', term: '10 Year' },
    ],
    rentRoll: [
      { tenant: 'Residential (180 units)', suite: 'Floors 5-25', sf: 162000, monthlyRent: 495000, annualRent: 5940000, rentPerSf: 36.67, leaseStart: '2025-01-01', leaseEnd: '2026-12-31', escalation: '3.5% Annual', status: 'Current' },
      { tenant: 'Citibank Branch', suite: 'Ground-A', sf: 8500, monthlyRent: 56667, annualRent: 680000, rentPerSf: 80.00, leaseStart: '2022-06-01', leaseEnd: '2037-05-31', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Equinox Fitness', suite: 'Levels 2-3', sf: 35000, monthlyRent: 145833, annualRent: 1750000, rentPerSf: 50.00, leaseStart: '2022-09-01', leaseEnd: '2037-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Nobu Restaurant', suite: 'Ground-B', sf: 12000, monthlyRent: 110000, annualRent: 1320000, rentPerSf: 110.00, leaseStart: '2023-01-01', leaseEnd: '2038-12-31', escalation: '10% every 5 yrs', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.40, resultingLtv: 0.70, resultingDebtYield: 0.086, pass: true },
      { name: 'Downside', vacancyShock: 0.12, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.16, resultingLtv: 0.79, resultingDebtYield: 0.070, pass: true },
      { name: 'Severe', vacancyShock: 0.22, rentDecline: 0.12, capRateExpansion: 1.00, resultingDscr: 0.92, resultingLtv: 0.92, resultingDebtYield: 0.053, pass: false },
    ],
    committeeMemo: {
      dealId: 'ALG-1014',
      status: 'Approved',
      preparedBy: 'Marcus Webb',
      committeeDate: '2025-12-08',
      riskRating: 'Low',
      conditions: ['Minimum 92% residential occupancy at closing', 'Flood insurance confirmation', 'Condo docs review by outside counsel'],
      approvers: [
        { name: 'Patricia Dunn', role: 'Chief Credit Officer', voted: true, vote: 'Approve' },
        { name: 'Michael Torres', role: 'Head of Originations', voted: true, vote: 'Approve' },
        { name: 'Angela Reeves', role: 'Risk Committee Chair', voted: true, vote: 'Approve' },
      ],
      sections: {
        executiveSummary: '$78M senior mortgage for 180-unit luxury mixed-use tower in Brickell, Miami. Stabilized asset with strong retail tenancy.',
        propertyDescription: '25-story mixed-use tower with 180 luxury residential units, ground-floor retail with Nobu and Citibank, and Equinox fitness.',
        marketAnalysis: 'Brickell continues to benefit from domestic migration trends and corporate relocations to South Florida.',
        cashFlowSummary: 'NOI of $6.71M driven by 96% residential occupancy and creditworthy retail tenants. Avg effective rent of $2,750/unit.',
        structure: 'Ten-year fixed rate at 5.92%. 30-year amortization schedule. Standard cash management.',
        riskFactors: 'Condo conversion risk mitigated by restrictive covenants; hurricane/flood exposure addressed by insurance.',
        recommendation: 'Approve. Strong asset in premier market with diversified income stream.',
      },
    },
    closingChecklist: [
      {
        category: 'Legal & Title',
        items: [
          { id: 'CL-1014-1', label: 'Title Insurance Policy', status: 'In Progress', responsible: 'First American Title', dueDate: '2026-03-25', notes: 'Easement exception under review' },
          { id: 'CL-1014-2', label: 'ALTA Survey', status: 'Complete', responsible: 'Surveyor — GeoPoint', dueDate: '2026-03-15' },
          { id: 'CL-1014-3', label: 'Opinion of Borrower Counsel', status: 'Pending', responsible: 'Greenberg Traurig', dueDate: '2026-03-28' },
          { id: 'CL-1014-4', label: 'UCC Filing', status: 'Pending', responsible: 'Lender Counsel', dueDate: '2026-04-01' },
        ],
      },
      {
        category: 'Diligence',
        items: [
          { id: 'CL-1014-5', label: 'Appraisal Final', status: 'Complete', responsible: 'CBRE', dueDate: '2026-03-10' },
          { id: 'CL-1014-6', label: 'Phase I ESA', status: 'Complete', responsible: 'ECS Environmental', dueDate: '2026-02-28' },
          { id: 'CL-1014-7', label: 'Property Condition Report', status: 'Complete', responsible: 'Partner Engineering', dueDate: '2026-03-05' },
          { id: 'CL-1014-8', label: 'Seismic / Wind Risk Assessment', status: 'Complete', responsible: 'AIR Worldwide', dueDate: '2026-03-01' },
        ],
      },
      {
        category: 'Financial & Insurance',
        items: [
          { id: 'CL-1014-9', label: 'Insurance Binder (Property & Liability)', status: 'In Progress', responsible: 'Borrower / AON', dueDate: '2026-03-30' },
          { id: 'CL-1014-10', label: 'Flood Insurance', status: 'Pending', responsible: 'Borrower', dueDate: '2026-03-30' },
          { id: 'CL-1014-11', label: 'Escrow Funding (Tax & Insurance)', status: 'Pending', responsible: 'Borrower', dueDate: '2026-04-05' },
        ],
      },
    ],
  },

  // ── ALG-1015 · Rate Lock · Industrial · Senior Mortgage ──────────────
  {
    id: 'ALG-1015',
    property: 'Centennial Logistics Park',
    type: 'Industrial',
    product: 'Senior Mortgage',
    location: 'Denver, CO',
    borrower: 'Front Range Industrial Partners',
    sponsor: 'Kevin O\'Brien',
    sponsorCredit: 758,
    loanAmount: 33.5,
    propertyValue: 50.7,
    ltv: 0.66,
    dscr: 1.61,
    debtYield: 0.097,
    stage: 'Rate Lock',
    stageIndex: 6,
    phase: 'Trading',
    assignee: 'Derek Hoffman',
    daysOpen: 88,
    daysInStatus: 12,
    rate: '5.78% Fixed',
    originated: '2025-12-21',
    priority: 'Medium',
    noi: 3.25,
    sf: 362000,
    yearBuilt: 2018,
    cashFlows: {
      historicNOI: 3.08,
      prospectiveNOI: 3.25,
      variability: 'Very Low',
      expenseRatio: 0.21,
      revenue: 4.11,
      expenses: 0.86,
      rents: 3.96,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Denver-Aurora-Lakewood',
      vacRate: 5.4,
      rentGrowth: 3.8,
      capRate: 5.75,
      absorption: 3800,
    },
    triggers: [
      { id: 'T-1015-1', type: 'Rate Lock Expiry', due: '2026-04-20', status: 'upcoming', severity: 'high', description: 'Rate lock expires — must close or extend' },
    ],
    documents: ['Rate Lock Confirmation', 'Appraisal', 'Title Commitment', 'ALTA Survey', 'Closing Instructions', 'Entity Docs'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 33.5, rate: '5.78% Fixed', spread: 295, index: 'UST', ltv: 0.66, maturity: '2036-01-01', term: '10 Year' },
    ],
    rentRoll: [
      { tenant: 'Grainger Industrial', suite: 'Bldg 1', sf: 180000, monthlyRent: 165000, annualRent: 1980000, rentPerSf: 11.00, leaseStart: '2019-01-01', leaseEnd: '2029-12-31', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Sysco Food Services', suite: 'Bldg 2', sf: 120000, monthlyRent: 115000, annualRent: 1380000, rentPerSf: 11.50, leaseStart: '2021-06-01', leaseEnd: '2031-05-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: 'Bldg 3', sf: 62000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.61, resultingLtv: 0.66, resultingDebtYield: 0.097, pass: true },
      { name: 'Downside', vacancyShock: 0.15, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.30, resultingLtv: 0.75, resultingDebtYield: 0.077, pass: true },
      { name: 'Severe', vacancyShock: 0.25, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 1.02, resultingLtv: 0.87, resultingDebtYield: 0.059, pass: false },
    ],
    committeeMemo: {
      dealId: 'ALG-1015',
      status: 'Approved',
      preparedBy: 'Derek Hoffman',
      committeeDate: '2025-12-12',
      riskRating: 'Low',
      conditions: ['Vacant space lease-up escrow of $500K', 'Minimum 85% occupancy covenant'],
      approvers: [
        { name: 'Patricia Dunn', role: 'Chief Credit Officer', voted: true, vote: 'Approve' },
        { name: 'Michael Torres', role: 'Head of Originations', voted: true, vote: 'Approve' },
        { name: 'Angela Reeves', role: 'Risk Committee Chair', voted: true, vote: 'Approve' },
      ],
      sections: {
        executiveSummary: '$33.5M senior mortgage for 362K SF industrial park along I-70 corridor in Denver. Strong logistics demand fundamentals.',
        propertyDescription: 'Three-building industrial park with 32-foot clear heights, cross-dock capability, and ESFR sprinkler systems.',
        marketAnalysis: 'Denver industrial market is one of the tightest in the Mountain West with sub-6% vacancy and sustained demand from e-commerce.',
        cashFlowSummary: 'In-place NOI of $3.25M with 83% occupancy. Below-market rents provide upside upon Bldg 3 lease-up.',
        structure: 'Ten-year fixed rate at 5.78%. 30-year amortization. Standard reserves.',
        riskFactors: 'Vacant Building 3 (17% of total); mitigated by strong market fundamentals and lease-up escrow.',
        recommendation: 'Approve with lease-up escrow condition.',
      },
    },
    closingChecklist: [
      {
        category: 'Legal & Title',
        items: [
          { id: 'CL-1015-1', label: 'Title Insurance Policy', status: 'In Progress', responsible: 'Chicago Title', dueDate: '2026-03-28' },
          { id: 'CL-1015-2', label: 'ALTA Survey', status: 'Complete', responsible: 'Merrick & Co', dueDate: '2026-03-10' },
          { id: 'CL-1015-3', label: 'Loan Documents Execution', status: 'Pending', responsible: 'Lender Counsel', dueDate: '2026-04-05' },
        ],
      },
      {
        category: 'Diligence',
        items: [
          { id: 'CL-1015-4', label: 'Appraisal Final', status: 'Complete', responsible: 'Cushman & Wakefield', dueDate: '2026-03-01' },
          { id: 'CL-1015-5', label: 'Phase I ESA', status: 'Complete', responsible: 'Terracon', dueDate: '2026-02-20' },
          { id: 'CL-1015-6', label: 'Property Condition Report', status: 'Complete', responsible: 'Partner Engineering', dueDate: '2026-02-25' },
        ],
      },
    ],
  },

  // ── ALG-1016 · Funded · Multifamily + Mezz combo (Senior piece) ──────
  {
    id: 'ALG-1016',
    property: 'Riverwalk at Tempe Town Lake',
    type: 'Multifamily',
    product: 'Senior Mortgage',
    location: 'Phoenix, AZ',
    borrower: 'Desert Bloom Residential LLC',
    sponsor: 'Alexandra Vasquez',
    sponsorCredit: 763,
    loanAmount: 85.0,
    propertyValue: 121.4,
    ltv: 0.70,
    dscr: 1.44,
    debtYield: 0.087,
    stage: 'Funded',
    stageIndex: 7,
    phase: 'Asset Management',
    assignee: 'Lisa Tran',
    daysOpen: 142,
    daysInStatus: 17,
    maturity: '2031-06-01',
    rate: 'SOFR+375',
    originated: '2025-10-28',
    priority: 'Medium',
    noi: 7.40,
    sf: 340000,
    units: 400,
    yearBuilt: 2021,
    cashFlows: {
      historicNOI: 7.12,
      prospectiveNOI: 7.40,
      variability: 'Low',
      expenseRatio: 0.38,
      revenue: 11.94,
      expenses: 4.54,
      rents: 11.52,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Phoenix-Mesa-Chandler',
      vacRate: 7.8,
      rentGrowth: 3.1,
      capRate: 5.25,
      absorption: 5100,
    },
    triggers: [
      { id: 'T-1016-1', type: 'Financial Reporting', due: '2026-03-31', status: 'pending', severity: 'medium', description: 'Q1 2026 operating statement due' },
      { id: 'T-1016-2', type: 'Insurance Renewal', due: '2026-04-15', status: 'upcoming', severity: 'low', description: 'Annual property insurance renewal' },
    ],
    documents: ['Executed Loan Agreement', 'Recorded Mortgage', 'Title Policy', 'Appraisal', 'Phase I ESA', 'Insurance Certificate', 'Rate Cap Agreement', 'Annual Operating Statement'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 85.0, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.70, maturity: '2031-06-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Unit Mix — Studios (80)', suite: 'Bldg 1', sf: 44000, monthlyRent: 144000, annualRent: 1728000, rentPerSf: 39.27, leaseStart: '2025-01-01', leaseEnd: '2026-06-30', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 1BR (200)', suite: 'Bldgs 1-3', sf: 160000, monthlyRent: 380000, annualRent: 4560000, rentPerSf: 28.50, leaseStart: '2025-03-01', leaseEnd: '2026-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 2BR (120)', suite: 'Bldgs 2-4', sf: 136000, monthlyRent: 312000, annualRent: 3744000, rentPerSf: 27.53, leaseStart: '2025-06-01', leaseEnd: '2027-05-31', escalation: '3% Annual', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.44, resultingLtv: 0.70, resultingDebtYield: 0.087, pass: true },
      { name: 'Downside', vacancyShock: 0.12, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 1.18, resultingLtv: 0.79, resultingDebtYield: 0.070, pass: true },
      { name: 'Severe', vacancyShock: 0.20, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 0.95, resultingLtv: 0.91, resultingDebtYield: 0.054, pass: false },
    ],
  },

  // ── ALG-1017 · Funded · Multifamily + Mezz combo (Mezzanine piece, same property) ─
  {
    id: 'ALG-1017',
    property: 'Riverwalk at Tempe Town Lake',
    type: 'Multifamily',
    product: 'Mezzanine',
    location: 'Phoenix, AZ',
    borrower: 'Desert Bloom Residential LLC',
    sponsor: 'Alexandra Vasquez',
    sponsorCredit: 763,
    loanAmount: 18.2,
    propertyValue: 121.4,
    ltv: 0.85,
    dscr: 1.18,
    debtYield: 0.072,
    stage: 'Funded',
    stageIndex: 7,
    phase: 'Asset Management',
    assignee: 'Lisa Tran',
    daysOpen: 142,
    daysInStatus: 17,
    maturity: '2031-06-01',
    rate: 'SOFR+600',
    originated: '2025-10-28',
    priority: 'Medium',
    noi: 7.40,
    sf: 340000,
    units: 400,
    yearBuilt: 2021,
    cashFlows: {
      historicNOI: 7.12,
      prospectiveNOI: 7.40,
      variability: 'Low',
      expenseRatio: 0.38,
      revenue: 11.94,
      expenses: 4.54,
      rents: 11.52,
      leaseVelocity: 'Strong',
    },
    market: {
      msa: 'Phoenix-Mesa-Chandler',
      vacRate: 7.8,
      rentGrowth: 3.1,
      capRate: 5.25,
      absorption: 5100,
    },
    triggers: [
      { id: 'T-1017-1', type: 'DSCR Test', due: '2026-03-31', status: 'pending', severity: 'high', description: 'Quarterly DSCR test — threshold 1.10x' },
    ],
    documents: ['Executed Mezzanine Loan Agreement', 'Intercreditor Agreement', 'Pledge Agreement', 'Insurance Certificate'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 85.0, rate: 'SOFR+375', spread: 375, index: 'SOFR', ltv: 0.70, maturity: '2031-06-01', term: '5 Year' },
      { type: 'Mezzanine', amount: 18.2, rate: 'SOFR+600', spread: 600, index: 'SOFR', ltv: 0.85, maturity: '2031-06-01', term: '5 Year' },
    ],
    rentRoll: [
      { tenant: 'Unit Mix — Studios (80)', suite: 'Bldg 1', sf: 44000, monthlyRent: 144000, annualRent: 1728000, rentPerSf: 39.27, leaseStart: '2025-01-01', leaseEnd: '2026-06-30', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 1BR (200)', suite: 'Bldgs 1-3', sf: 160000, monthlyRent: 380000, annualRent: 4560000, rentPerSf: 28.50, leaseStart: '2025-03-01', leaseEnd: '2026-08-31', escalation: '3% Annual', status: 'Current' },
      { tenant: 'Unit Mix — 2BR (120)', suite: 'Bldgs 2-4', sf: 136000, monthlyRent: 312000, annualRent: 3744000, rentPerSf: 27.53, leaseStart: '2025-06-01', leaseEnd: '2027-05-31', escalation: '3% Annual', status: 'Current' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.18, resultingLtv: 0.85, resultingDebtYield: 0.072, pass: true },
      { name: 'Downside', vacancyShock: 0.12, rentDecline: 0.05, capRateExpansion: 0.50, resultingDscr: 0.98, resultingLtv: 0.95, resultingDebtYield: 0.057, pass: false },
      { name: 'Severe', vacancyShock: 0.20, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 0.81, resultingLtv: 1.08, resultingDebtYield: 0.044, pass: false },
    ],
  },

  // ── ALG-1018 · Funded · Office · B-Note ──────────────────────────────
  {
    id: 'ALG-1018',
    property: 'One Congress Plaza',
    type: 'Office',
    product: 'B-Note',
    location: 'Chicago, IL',
    borrower: 'Wacker Drive Capital',
    sponsor: 'Theodore Jankowski',
    sponsorCredit: 708,
    loanAmount: 12.5,
    propertyValue: 82.0,
    ltv: 0.80,
    dscr: 1.15,
    debtYield: 0.075,
    stage: 'Funded',
    stageIndex: 7,
    phase: 'Asset Management',
    assignee: 'Marcus Webb',
    daysOpen: 178,
    daysInStatus: 21,
    maturity: '2033-03-01',
    rate: 'SOFR+600',
    originated: '2025-09-22',
    priority: 'High',
    noi: 6.15,
    sf: 380000,
    yearBuilt: 1985,
    cashFlows: {
      historicNOI: 6.42,
      prospectiveNOI: 6.15,
      variability: 'High',
      expenseRatio: 0.47,
      revenue: 11.60,
      expenses: 5.45,
      rents: 10.85,
      leaseVelocity: 'Weak',
    },
    market: {
      msa: 'Chicago-Naperville-Elgin',
      vacRate: 19.6,
      rentGrowth: -0.5,
      capRate: 7.50,
      absorption: -2100,
    },
    triggers: [
      { id: 'T-1018-1', type: 'DSCR Test', due: '2026-03-15', status: 'overdue', severity: 'high', description: 'Quarterly DSCR test — breached 1.10x threshold' },
      { id: 'T-1018-2', type: 'Lease Rollover', due: '2026-06-30', status: 'upcoming', severity: 'high', description: 'Kirkland & Ellis lease (82K SF) expiring — renewal status unknown' },
      { id: 'T-1018-3', type: 'Financial Reporting', due: '2026-03-31', status: 'pending', severity: 'medium', description: 'Annual operating statement and rent roll due' },
    ],
    documents: ['Executed B-Note Agreement', 'Intercreditor Agreement', 'Participation Agreement', 'Title Policy', 'Appraisal', 'Annual Operating Statement', 'Watchlist Memo'],
    capitalStack: [
      { type: 'Senior Mortgage', amount: 53.1, rate: 'SOFR+325', spread: 325, index: 'SOFR', ltv: 0.65, maturity: '2033-03-01', term: '7 Year' },
      { type: 'B-Note', amount: 12.5, rate: 'SOFR+600', spread: 600, index: 'SOFR', ltv: 0.80, maturity: '2033-03-01', term: '7 Year' },
    ],
    rentRoll: [
      { tenant: 'Kirkland & Ellis LLP', suite: '3000-3800', sf: 82000, monthlyRent: 451000, annualRent: 5412000, rentPerSf: 66.00, leaseStart: '2016-07-01', leaseEnd: '2026-06-30', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Northern Trust Corp', suite: '1500-1800', sf: 48000, monthlyRent: 228000, annualRent: 2736000, rentPerSf: 57.00, leaseStart: '2021-01-01', leaseEnd: '2031-12-31', escalation: '2% Annual', status: 'Current' },
      { tenant: 'Aon PLC', suite: '2000-2200', sf: 38000, monthlyRent: 190000, annualRent: 2280000, rentPerSf: 60.00, leaseStart: '2022-04-01', leaseEnd: '2029-03-31', escalation: '2.5% Annual', status: 'Current' },
      { tenant: 'Vacant', suite: '1000-1200', sf: 42000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
      { tenant: 'Vacant', suite: '2500-2600', sf: 28000, monthlyRent: 0, annualRent: 0, rentPerSf: 0, leaseStart: '', leaseEnd: '', escalation: '', status: 'Vacant' },
    ],
    stressTests: [
      { name: 'Base', vacancyShock: 0.05, rentDecline: 0, capRateExpansion: 0, resultingDscr: 1.15, resultingLtv: 0.80, resultingDebtYield: 0.075, pass: true },
      { name: 'Downside', vacancyShock: 0.20, rentDecline: 0.10, capRateExpansion: 1.00, resultingDscr: 0.82, resultingLtv: 0.99, resultingDebtYield: 0.049, pass: false },
      { name: 'Severe', vacancyShock: 0.30, rentDecline: 0.15, capRateExpansion: 1.75, resultingDscr: 0.62, resultingLtv: 1.18, resultingDebtYield: 0.035, pass: false },
    ],
  },

];

/** The curated book is kept in $MM (62.5 = $62.5M) and declares it, so no
 *  screen has to guess a deal's unit from the size of its numbers. */
export const ANTARCTICA_DEALS: Loan[] = CURATED_DEALS.map((deal) => ({ ...deal, amountUnit: 'USD_MM' as const }))

// ---------------------------------------------------------------------------
// 2. PIPELINE_METRICS
// ---------------------------------------------------------------------------

// Computed from ANTARCTICA_DEALS above
// Total volume: 62.5+47+145+38.5+18.5+54+72+22+28.5+95+56+41+185+78+33.5+85+18.2+12.5 = 1091.7
// In closing (Rate Lock + Funded): 1014,1015,1016,1017,1018 = 5
// Overdue triggers: 1 (ALG-1018 T-1018-1)
// YTD originations (funded deals): 85+18.2+12.5 = 115.7
// Avg days to close (funded): (142+142+178)/3 = 154

export const PIPELINE_METRICS: PipelineMetrics = {
  totalVolume: 1091.7,
  dealCount: 18,
  avgLTV: 0.706,
  avgDSCR: 1.43,
  inClosing: 5,
  overdueTriggers: 1,
  ytdOriginations: 115.7,
  avgDaysToClose: 154,
  totalAum: 115.7,
  wtdAvgDebtYield: 0.088,
};

// ---------------------------------------------------------------------------
// 3. STAGE_DISTRIBUTION — waterfall chart data
// ---------------------------------------------------------------------------

export const STAGE_DISTRIBUTION: { stage: LoanStage; count: number; volume: number }[] = [
  { stage: 'Triage', count: 3, volume: 254.5 },
  { stage: 'Post-screen Quote', count: 3, volume: 111.0 },
  { stage: 'Term Sheet Issued', count: 2, volume: 94.0 },
  { stage: 'Term Sheet Signed', count: 2, volume: 123.5 },
  { stage: 'IC / Committee Approved', count: 2, volume: 97.0 },
  { stage: 'IC / Committee Approved', count: 1, volume: 185.0 },
  { stage: 'Rate Lock', count: 2, volume: 111.5 },
  { stage: 'Funded', count: 3, volume: 115.7 },
];

// ---------------------------------------------------------------------------
// 4. PRODUCT_MIX — donut chart data
// ---------------------------------------------------------------------------

export const PRODUCT_MIX: { product: ProductType; count: number; volume: number; pct: number }[] = [
  { product: 'Senior Mortgage', count: 10, volume: 667.5, pct: 61.1 },
  { product: 'Large Loan', count: 2, volume: 330.0, pct: 30.2 },
  { product: 'B-Note', count: 2, volume: 31.0, pct: 2.8 },
  { product: 'Mezzanine', count: 2, volume: 40.2, pct: 3.7 },
  { product: 'Ground Lease', count: 2, volume: 23.0, pct: 2.1 },
];

// ---------------------------------------------------------------------------
// 5. MARKET_RATES — current benchmarks
// ---------------------------------------------------------------------------

export const MARKET_RATES = {
  sofr: 4.33,
  ust10y: 4.18,
  cmbsAaaSpread: 95,
  cmbsBbbSpread: 285,
  multifamilyCapRate: 5.25,
  officeCapRate: 7.50,
  industrialCapRate: 5.75,
  retailCapRate: 6.50,
  hotelCapRate: 8.25,
  seniorLivingCapRate: 6.75,
} as const;

// ---------------------------------------------------------------------------
// 6. RECENT_ALERTS — command center feed
// ---------------------------------------------------------------------------

export const RECENT_ALERTS: {
  id: string;
  dealId: string;
  property: string;
  type: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
  timestamp: string;
}[] = [
  {
    id: 'ALERT-001',
    dealId: 'ALG-1018',
    property: 'One Congress Plaza',
    type: 'DSCR Breach',
    severity: 'high',
    message: 'Quarterly DSCR fell to 1.08x, below 1.10x trigger threshold. Watchlist review initiated.',
    timestamp: '2026-03-15T09:22:00Z',
  },
  {
    id: 'ALERT-002',
    dealId: 'ALG-1018',
    property: 'One Congress Plaza',
    type: 'Lease Rollover',
    severity: 'high',
    message: 'Kirkland & Ellis lease (82,000 SF, 22% of NRA) expiring June 2026. No renewal indication received.',
    timestamp: '2026-03-14T14:45:00Z',
  },
  {
    id: 'ALERT-003',
    dealId: 'ALG-1014',
    property: 'The Brickell Promenade',
    type: 'Rate Lock Expiry',
    severity: 'high',
    message: 'Rate lock expires April 14. Title easement exception must clear before closing.',
    timestamp: '2026-03-18T08:10:00Z',
  },
  {
    id: 'ALERT-004',
    dealId: 'ALG-1013',
    property: 'Kendall Square Innovation Center',
    type: 'Rate Lock Deadline',
    severity: 'high',
    message: 'Borrower must elect rate lock by April 10. $185M large loan — coordinate with trading desk.',
    timestamp: '2026-03-17T11:30:00Z',
  },
  {
    id: 'ALERT-005',
    dealId: 'ALG-1011',
    property: 'The Ellington at Uptown',
    type: 'Commitment Expiry',
    severity: 'high',
    message: 'Commitment letter expires April 15. Awaiting borrower countersignature.',
    timestamp: '2026-03-16T10:05:00Z',
  },
  {
    id: 'ALERT-006',
    dealId: 'ALG-1017',
    property: 'Riverwalk at Tempe Town Lake',
    type: 'DSCR Test',
    severity: 'medium',
    message: 'Quarterly mezzanine DSCR test due March 31. Current DSCR at 1.18x vs 1.10x threshold.',
    timestamp: '2026-03-18T07:45:00Z',
  },
  {
    id: 'ALERT-007',
    dealId: 'ALG-1003',
    property: 'Gateway Logistics Center',
    type: 'New Deal',
    severity: 'low',
    message: '$145M large loan received — Phoenix industrial portfolio. Assigned to Sarah Chen.',
    timestamp: '2026-03-14T16:20:00Z',
  },
  {
    id: 'ALERT-008',
    dealId: 'ALG-1010',
    property: 'Meridian Tower',
    type: 'Appraisal',
    severity: 'medium',
    message: 'CBRE appraisal draft expected March 25 for $95M NYC office. Underwriting on hold pending valuation.',
    timestamp: '2026-03-13T13:55:00Z',
  },
];

// ---------------------------------------------------------------------------
// 7. VALUATION_RECORDS
// ---------------------------------------------------------------------------

export const VALUATION_RECORDS: ValuationRecord[] = [
  {
    propertyId: 'ALG-1016',
    propertyName: 'Riverwalk at Tempe Town Lake',
    valuationDate: '2025-10-15',
    appraiser: 'JLL Valuation Advisory',
    asIsValue: 121.4,
    stabilizedValue: 128.5,
    capRate: 5.25,
    discountRate: 7.50,
    terminalCapRate: 5.75,
    noi: 7.40,
    occupancy: 0.96,
    status: 'Finalized',
    priorValue: 115.0,
    valueChange: 6.4,
    valueChangePct: 5.6,
  },
  {
    propertyId: 'ALG-1018',
    propertyName: 'One Congress Plaza',
    valuationDate: '2025-09-01',
    appraiser: 'Cushman & Wakefield',
    asIsValue: 82.0,
    stabilizedValue: 96.0,
    capRate: 7.50,
    discountRate: 9.25,
    terminalCapRate: 8.00,
    noi: 6.15,
    occupancy: 0.82,
    status: 'Finalized',
    priorValue: 94.0,
    valueChange: -12.0,
    valueChangePct: -12.8,
  },
  {
    propertyId: 'ALG-1013',
    propertyName: 'Kendall Square Innovation Center',
    valuationDate: '2025-12-20',
    appraiser: 'CBRE Valuation & Advisory',
    asIsValue: 264.3,
    stabilizedValue: 278.0,
    capRate: 5.50,
    discountRate: 7.00,
    terminalCapRate: 6.00,
    noi: 15.17,
    occupancy: 0.964,
    status: 'Finalized',
  },
  {
    propertyId: 'ALG-1010',
    propertyName: 'Meridian Tower',
    valuationDate: '2026-03-25',
    appraiser: 'CBRE Valuation & Advisory',
    asIsValue: 146.2,
    stabilizedValue: 162.0,
    capRate: 6.80,
    discountRate: 8.50,
    terminalCapRate: 7.25,
    noi: 8.07,
    occupancy: 0.87,
    status: 'Awaiting Draft',
  },
  {
    propertyId: 'ALG-1007',
    propertyName: 'Inland Empire Distribution Hub',
    valuationDate: '2026-02-28',
    appraiser: 'Newmark Valuation',
    asIsValue: 102.9,
    stabilizedValue: 115.0,
    capRate: 5.00,
    discountRate: 6.75,
    terminalCapRate: 5.50,
    noi: 6.34,
    occupancy: 0.856,
    status: 'In Review',
  },
  {
    propertyId: 'ALG-1014',
    propertyName: 'The Brickell Promenade',
    valuationDate: '2026-03-10',
    appraiser: 'CBRE Valuation & Advisory',
    asIsValue: 111.4,
    stabilizedValue: 118.0,
    capRate: 5.40,
    discountRate: 7.25,
    terminalCapRate: 5.90,
    noi: 6.71,
    occupancy: 0.97,
    status: 'Finalized',
  },
];

// ---------------------------------------------------------------------------
// 8. DEVELOPMENT_PROJECTS
// ---------------------------------------------------------------------------

export const DEVELOPMENT_PROJECTS: DevelopmentFeasibility[] = [
  {
    projectName: 'Hudson Yards West Tower — NYC',
    totalCost: 425.0,
    gdv: 580.0,
    margin: 0.365,
    irr: 18.2,
    npv: 62.4,
    profitOnCost: 0.365,
    equityRequired: 148.8,
    debtRequired: 276.2,
    constructionPeriod: 36,
    scenarios: [
      { name: 'Base', margin: 0.365, irr: 18.2, npv: 62.4 },
      { name: 'Downside (15% rent decline)', margin: 0.22, irr: 12.1, npv: 28.5 },
      { name: 'Severe (25% rent decline + 10% cost overrun)', margin: 0.08, irr: 5.4, npv: -8.2 },
    ],
  },
  {
    projectName: 'Scottsdale Airpark Life Science Campus — Phoenix',
    totalCost: 195.0,
    gdv: 268.0,
    margin: 0.374,
    irr: 21.5,
    npv: 38.7,
    profitOnCost: 0.374,
    equityRequired: 68.3,
    debtRequired: 126.7,
    constructionPeriod: 24,
    scenarios: [
      { name: 'Base', margin: 0.374, irr: 21.5, npv: 38.7 },
      { name: 'Downside (12% rent decline)', margin: 0.25, irr: 15.8, npv: 22.1 },
      { name: 'Severe (20% rent decline + 8% cost overrun)', margin: 0.10, irr: 7.2, npv: -3.4 },
    ],
  },
  {
    projectName: 'Midtown East Multifamily — Atlanta',
    totalCost: 82.0,
    gdv: 108.5,
    margin: 0.323,
    irr: 19.8,
    npv: 14.2,
    profitOnCost: 0.323,
    equityRequired: 28.7,
    debtRequired: 53.3,
    constructionPeriod: 20,
    scenarios: [
      { name: 'Base', margin: 0.323, irr: 19.8, npv: 14.2 },
      { name: 'Downside (10% rent decline)', margin: 0.21, irr: 14.1, npv: 7.8 },
      { name: 'Severe (18% rent decline + 12% cost overrun)', margin: 0.05, irr: 4.8, npv: -4.1 },
    ],
  },
];
